import { IconButton } from '@mui/material';
import { ChevronRight, ExpandMore, SubdirectoryArrowRight } from '@tempo-io/sui-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwimlaneSlot } from '@/components/ui/swimlane/context';
import {
  MAX_DISPLAYED_GRAND_CHILDREN,
  MAX_DISPLAYED_SUB_ITEMS,
  SUBITEM_GAP,
  SUBITEM_HEIGHT,
} from '@/components/ui/swimlane/layout/constants';
import { bindOpenCardDetailsHandler } from '@/components/ui/swimlane/layout/hooks/useCardEventHandlers';
import type { ICardProps, SubItem, TColumnID, TRowID } from '@/components/ui/swimlane/types';

import { AddSubItemInput } from './AddSubItemInput';
import { SubItemHeader } from './SubItemHeader';

const DEFAULT_SUB_ITEM_BG = '#E5F0FB';

const subItemPillBaseStyle: React.CSSProperties = {
  borderRadius: '4px',
  padding: '4px 10px',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const moreItemsStyle: React.CSSProperties = {
  padding: '2px 0',
  fontSize: '11px',
  lineHeight: '16px',
  color: '#6B6B6B',
  fontStyle: 'italic',
};

const subItemsListStyle: React.CSSProperties = {
  padding: 0,
  margin: 0,
  listStyle: 'none',
};

const subItemHeaderRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '4px',
  minHeight: SUBITEM_HEIGHT.label,
};

const subItemTitleButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  flex: 1,
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
  padding: 0,
  color: 'inherit',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const grandChildListStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

const grandChildButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 0',
  width: '100%',
  textAlign: 'left',
  fontSize: '12px',
  lineHeight: '16px',
  color: 'inherit',
};

type SubItemRowProps = {
  subItem: SubItem;
  rowID: TRowID;
  columnID: TColumnID;
  backgroundColor?: string;
};

function SubItemRow({ subItem, rowID, columnID, backgroundColor }: SubItemRowProps) {
  const { t } = useTranslation();
  const [childrenExpanded, setChildrenExpanded] = useState(true);
  const grandChildren = subItem.subItems ?? [];
  const hasGrandChildren = grandChildren.length > 0;
  const visibleGrandChildren = grandChildren.slice(0, MAX_DISPLAYED_GRAND_CHILDREN);
  const hiddenGrandChildCount = Math.max(0, grandChildren.length - MAX_DISPLAYED_GRAND_CHILDREN);
  const pillStyle: React.CSSProperties = {
    ...subItemPillBaseStyle,
    backgroundColor: backgroundColor ?? DEFAULT_SUB_ITEM_BG,
  };

  return (
    <li style={{ listStyle: 'none', marginBottom: SUBITEM_GAP.label }}>
      <div style={pillStyle}>
        <div style={subItemHeaderRowStyle}>
          <button
            {...bindOpenCardDetailsHandler({ itemID: subItem.id, columnID, rowID })}
            data-dd-action-name="SL sub item - redacted"
            className="fs-exclude"
            style={subItemTitleButtonStyle}
          >
            {subItem.title}
          </button>
          {hasGrandChildren && (
            <IconButton
              size="small"
              onClick={() => setChildrenExpanded((expanded) => !expanded)}
              aria-label={t(
                ($) =>
                  childrenExpanded
                    ? $.views.visualization.subItems.collapseAriaLabel
                    : $.views.visualization.subItems.expandAriaLabel,
                { title: subItem.title },
              )}
              aria-expanded={childrenExpanded}
              sx={{ padding: '2px' }}
            >
              {childrenExpanded ? <ExpandMore size={16} /> : <ChevronRight size={16} />}
            </IconButton>
          )}
        </div>
        {hasGrandChildren && childrenExpanded && (
          <ul style={grandChildListStyle}>
            {visibleGrandChildren.map((grandChild) => (
              <li key={grandChild.id} style={{ listStyle: 'none' }}>
                <button
                  {...bindOpenCardDetailsHandler({
                    itemID: grandChild.id,
                    columnID,
                    rowID,
                  })}
                  data-dd-action-name="SL grand-child item - redacted"
                  className="fs-exclude"
                  style={grandChildButtonStyle}
                >
                  <span style={{ flexShrink: 0, display: 'inline-flex' }}>
                    <SubdirectoryArrowRight size={14} />
                  </span>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {grandChild.title}
                  </span>
                </button>
              </li>
            ))}
            {hiddenGrandChildCount > 0 && (
              <li style={{ listStyle: 'none' }}>
                <span
                  style={{
                    ...moreItemsStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span style={{ flexShrink: 0, display: 'inline-flex' }}>
                    <SubdirectoryArrowRight size={14} />
                  </span>
                  {t(($) => $.views.visualization.subItems.moreItems, {
                    count: hiddenGrandChildCount,
                  })}
                </span>
              </li>
            )}
          </ul>
        )}
      </div>
    </li>
  );
}

type SubItemsProps = ICardProps & {
  expanded: boolean;
  subItems: SubItem[];
  readOnly: boolean;
  maxSubItemsPerItem: number;
  subItemsBackgroundColor?: string;
};

export function SubItems({
  itemID,
  rowID,
  columnID,
  expanded,
  subItems,
  readOnly,
  maxSubItemsPerItem,
  subItemsBackgroundColor,
}: SubItemsProps) {
  const { t } = useTranslation();
  const [showTextField, setShowTextField] = useState(false);
  const { component: SlotAddSubItemInput = AddSubItemInput, props: addSubItemInputSlotProps } =
    useSwimlaneSlot('addSubItemInput');
  const canAdd = !readOnly && subItems.length < maxSubItemsPerItem;

  const handleAddButton = () => {
    setShowTextField(!showTextField);
  };

  return (
    <div
      style={{
        maxWidth: '100%',
      }}
    >
      {expanded && (
        <div style={{ padding: 0 }}>
          <SubItemHeader count={subItems.length} onAdd={handleAddButton} canAdd={canAdd} />
          <ul style={subItemsListStyle}>
            {canAdd && (showTextField || subItems.length === 0) && (
              <div style={subItems.length > 0 ? { marginBottom: SUBITEM_GAP.label } : undefined}>
                <SlotAddSubItemInput
                  itemID={itemID}
                  rowID={rowID}
                  columnID={columnID}
                  {...addSubItemInputSlotProps}
                />
              </div>
            )}
            {subItems.slice(0, MAX_DISPLAYED_SUB_ITEMS).map((subItem) => (
              <SubItemRow
                key={subItem.id}
                subItem={subItem}
                rowID={rowID}
                columnID={columnID}
                backgroundColor={subItemsBackgroundColor}
              />
            ))}
            {subItems.length > MAX_DISPLAYED_SUB_ITEMS && (
              <li
                style={{
                  listStyle: 'none',
                  marginBottom: SUBITEM_GAP.label,
                  padding: '0 10px',
                }}
              >
                <span style={moreItemsStyle}>
                  {t(($) => $.views.visualization.subItems.moreItems, {
                    count: subItems.length - MAX_DISPLAYED_SUB_ITEMS,
                  })}
                </span>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
